/* eslint-disable @typescript-eslint/no-explicit-any */
import { useMiiaFwS3 } from '@/modules/miiaFw/composables/useMiiaFwS3'
import MiiaFwConfig from '@/configs/config'
import { useS3Utils } from './useS3Utils'
import type { Directory } from '@/types/accessor/s3/directory'
import { useLoadingView } from '../../../modules/miiaFw/composables/useLoadingView'
import type { BucketParameter } from '@/modules/miiaFw/MiiaFwS3'
import * as s3AccessTypes from '@/types/accessor/api/s3/s3'
import * as apiGwFuncs from '@/accessors/api/disasterCommonS3Api'
import { FETCH_PUT_ERROR_CODE } from '@/constants/common/errorCode'
import { handleError } from '@/accessors/util/errorHandler'

export const useS3Accessor = () => {
  const { generateDirectory } = useS3Utils()
  const { setLoadingView } = useLoadingView()
  const { downloadObject, getFileList } = useMiiaFwS3()

  const getFileFromS3 = async (fileName: string, prefix: string | null = null): Promise<File> => {
    const bucketParam = {
      name: MiiaFwConfig.config.s3Access.contentsBucket,
      prefix: prefix ? prefix : '',
      delimiter: '/'
    }
    const result = await downloadObject(bucketParam, fileName, 'blob', 600000)
    setLoadingView(false)
    return new File([result], fileName, { type: result.type })
  }

  /**
   * S3 Bucketへデータをアップロードする処理の本体(閉域網用)。
   * @param bucketParam バケットを指定するパラメータ
   * @param fileName ファイル名(S3上でのフルパス)
   * @param file オブジェクトデータ
   * @returns Promise<void>
   */
  const uploadFile = async (
    bucketParam: BucketParameter,
    fileName: string,
    file: File | Blob
  ): Promise<void> => {
    const rawUrl = bucketParam.prefix + fileName
    const presignedUrls = await apiGwFuncs
      .fetchPresignedUrl({
        method: 'put',
        bucket: bucketParam.name,
        keys: [rawUrl]
      })
      .then(res => (res as s3AccessTypes.ResFetchPresignedUrl).presignedUrls)
    if (presignedUrls.length === 0) return
    await fetch(presignedUrls[0], {
      method: 'PUT',
      body: file
    })
      .then(async res => {
        // fetchは400番系などのエラーが出てもthenに入るので、okプロパティでレスポンスが正常かどうかを判定する
        if (res.ok) return res
        else
          return await handleError(
            FETCH_PUT_ERROR_CODE,
            'S3 Bucketへのアップロードに失敗しました。'
          )
      })
      .catch(async err => {
        console.error(err)
        return await handleError(FETCH_PUT_ERROR_CODE, 'S3 Bucketへのアップロードに失敗しました。')
      })
  }

  /**
   * S3 Bucket内のファイルの削除処理を行う。
   * @param bucketParam バケットを指定するパラメータ
   * @param fileName ファイル名
   * @returns Promise<void>
   */
  const deleteFile = async (bucketParam: BucketParameter, fileName: string): Promise<void> => {
    return await apiGwFuncs
      .deleteFiles({
        bucket: bucketParam.name,
        keys: [fileName]
      })
      .then(() => {})
  }

  /**
   * ファイル一覧の取得
   * @param {Directory} directoryParams 検索対象のS3ディレクトリ
   * @returns Promise<void>
   */
  const getFileListFromS3 = async (directoryParams: Directory): Promise<any> => {
    const prefix = generateDirectory({
      govCode: directoryParams.govCode,
      disasterId: directoryParams.disasterId
    })

    return await new Promise((resolve, reject) => {
      getFileList({
        name: MiiaFwConfig.config.s3Access.contentsBucket,
        prefix: prefix,
        delimiter: '/'
      })
        .then((result: unknown) => {
          resolve(result)
        })
        .catch((error: any) => {
          reject(error)
        })
    })
  }

  /**
   * 証明書付きURLを取得する
   * @param params S3APIの'getPresignedUrl'関数のリクエストパラメータ
   * @returns 証明書付きURLの一覧 or エラー
   */
  const fetchPresignedUrls = async (
    params: s3AccessTypes.ReqFetchPresignedUrl
  ): Promise<s3AccessTypes.FetchPresignedUrlResponse> => {
    return await apiGwFuncs.fetchPresignedUrl(params)
  }

  /**
   * key[]を取得して、証明書付きURLの一覧を取得する
   * @param bucketParam バケットを指定するパラメータ
   * @returns 証明書付きURLの一覧 or エラー
   */
  const fetchKeysAndPresignedUrls = async (
    bucketParam: s3AccessTypes.ReqFetchKeys
  ): Promise<s3AccessTypes.FetchPresignedUrlResponse> => {
    return await apiGwFuncs.fetchKeys(bucketParam).then(async res => {
      const resFetchKeys = res as s3AccessTypes.ResFetchKeys
      if (resFetchKeys.keys.length === 0) return { presignedUrls: [] } // 不要なAPIコール回避
      return await fetchPresignedUrls({
        method: 'get',
        bucket: bucketParam.bucket,
        keys: resFetchKeys.keys
      })
    })
  }

  return {
    uploadFile,
    getFileFromS3,
    getFileListFromS3,
    deleteFile,
    fetchPresignedUrls,
    fetchKeysAndPresignedUrls
  }
}
